import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Inbox, Target, AlertCircle, LogOut, Settings, ChevronLeft, ChevronRight, Shield, Bot, LucideIcon } from 'lucide-react';
import { NotificationDrawer } from '@/components/NotificationDrawer';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PushNotifications } from '@/components/PushNotifications';
import { ProfileDrawer } from '@/components/ProfileDrawer';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/logo.png';
import { cn } from '@/lib/utils';

interface AppLayoutProps {
  children: ReactNode;
}

interface MenuItem {
  id: string;
  icon?: LucideIcon;
  label: string;
  path: string;
  dataTour: string;
  isSentinel?: boolean;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { signOut, profile, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!user) return;
    const checkAdmin = async () => {
      const { data } = await supabase.from('user_roles').select('role').eq('user_id', user.id).single();
      setIsAdmin(data?.role === 'admin');
    };
    checkAdmin();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const menuItems: MenuItem[] = [
    { id: 'inbox', icon: Inbox, label: 'Inbox', path: '/inbox', dataTour: 'inbox' },
    { id: 'exceptions', icon: AlertCircle, label: 'Exceptions', path: '/exceptions', dataTour: 'exceptions' },
    { id: 'sentinel', label: 'Sentinel AI', path: '/sentinel', dataTour: 'sentinel', isSentinel: true },
    { id: 'runway', icon: Target, label: 'Runway', path: '/runway', dataTour: 'runway' },
    { id: 'settings', icon: Settings, label: 'Settings', path: '/settings', dataTour: 'settings' },
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin', path: '/admin', dataTour: 'admin' }] : []),
  ];

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-card/95 backdrop-blur-xl border-b border-border/50 flex items-center px-4 gap-3 z-50">
        <img src={logo} alt="SENTINEL" className="h-8 w-8 brightness-0 dark:brightness-100 dark:invert" />
        <span className="text-lg font-bold tracking-tight">SENTINEL</span>
        <div className="ml-auto flex items-center gap-1">
          <NotificationDrawer />
          <ProfileDrawer />
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* Desktop Sidebar */}
      <aside 
        className={cn(
          "hidden md:flex bg-sidebar border-r border-sidebar-border flex-col shadow-card transition-all duration-300 ease-in-out relative",
          sidebarCollapsed ? 'w-20' : 'w-64'
        )}
      >
        {/* Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-6 z-50 w-6 h-6 rounded-full bg-card border border-border shadow-md hover:bg-surface-hover hover:scale-110 transition-all"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </Button>

        {/* Logo */}
        <div className={cn(
          "px-4 py-3 flex items-center mb-2 transition-all duration-300",
          sidebarCollapsed ? 'justify-center gap-0' : 'gap-3'
        )}>
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 p-1">
            <img src={logo} alt="SENTINEL Logo" className="w-full h-full object-contain brightness-0 dark:brightness-100 dark:invert" />
          </div>
          <span className={cn(
            "text-2xl font-bold tracking-tight transition-all duration-300 whitespace-nowrap",
            sidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100'
          )}>
            SENTINEL
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 space-y-0.5 px-3 py-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.id}
                data-tour={item.dataTour}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 768) setSidebarCollapsed(false);
                }}
                className={cn(
                  "w-full flex items-center rounded-2xl transition-all duration-300 group",
                  sidebarCollapsed ? 'justify-center px-2 py-2.5' : 'space-x-3 px-4 py-2.5',
                  isActive
                    ? 'bg-gradient-primary text-white shadow-glow'
                    : 'text-sidebar-foreground hover:bg-surface-hover hover:translate-x-1'
                )}
                title={sidebarCollapsed ? item.label : undefined}
              >
                {item.isSentinel ? (
                  <div className={cn(
                    "w-6 h-6 rounded-lg flex items-center justify-center",
                    isActive ? "" : "bg-gradient-primary"
                  )}>
                    <img src={logo} alt="Sentinel" className="w-4 h-4 brightness-0 invert" />
                  </div>
                ) : Icon ? (
                  <Icon size={20} className="flex-shrink-0" />
                ) : null}
                <span className={cn(
                  "font-medium transition-all duration-300 whitespace-nowrap",
                  sidebarCollapsed ? 'opacity-0 w-0' : 'opacity-100'
                )}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className={cn(
          "px-3 py-3 border-t border-sidebar-border bg-surface/50 transition-all duration-300",
          sidebarCollapsed && 'px-2'
        )}>
          {!sidebarCollapsed && (
            <div className="flex items-center justify-between mb-3 gap-2">
              <ThemeToggle />
              <PushNotifications />
              <NotificationDrawer />
            </div>
          )}
          <div className={cn(
            "flex items-center",
            sidebarCollapsed ? 'flex-col gap-2' : 'justify-between'
          )}>
            <div className={cn(
              "flex items-center transition-all duration-300",
              sidebarCollapsed ? 'flex-col gap-2' : 'gap-3'
            )}>
              {profile?.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={profile?.name || 'User'} 
                  className="w-10 h-10 rounded-2xl object-cover shadow-glow flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-2xl bg-gradient-primary flex items-center justify-center font-bold text-lg shadow-glow flex-shrink-0">
                  <span className="text-white">{profile?.name?.[0]?.toUpperCase() || 'U'}</span>
                </div>
              )}
              {!sidebarCollapsed && (
                <div>
                  <p className="text-sm font-medium truncate max-w-[120px]">{profile?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                    {profile?.subscription || 'free'}
                    {profile?.subscription === 'pro' && (
                      <span className="px-1.5 py-0.5 rounded-lg bg-primary/20 text-primary text-[10px] font-bold">PRO</span>
                    )}
                  </p>
                </div>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleSignOut} 
              className="hover:bg-surface-hover flex-shrink-0 rounded-xl"
              title="Sign Out"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main 
        key={location.pathname}
        className="flex-1 flex flex-col overflow-auto pt-16 pb-[calc(72px+env(safe-area-inset-bottom,0px))] md:pt-0 md:pb-0 page-enter"
      >
        {children}
      </main>
    </div>
  );
}
